import { type FC, useEffect, useMemo, useRef, useState } from "react";

import { Button, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useGetEvalReviewForRun, useScoreEvalReviewItem } from "@api/rest/types-and-hooks";
import { FailureTagPicker } from "@components/EvalReview/FailureTagPicker";
import { KeyLegend } from "@components/EvalReview/KeyLegend";
import { RuleCheckList } from "@components/EvalReview/RuleCheckList";
import { ScoreGrid } from "@components/EvalReview/ScoreGrid";
import { FAILURE_TAGS, MetricType, REVIEW_KEYS, SCORE_VALUES, StandardSource } from "@components/EvalReview/constants";
import type { CriterionKey } from "@components/EvalRubric/constants";
import { orderCriteria } from "@components/EvalRubric/constants";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface RunScorePanelProps {
  runId: number;
  /** The output as shown to the reviewer; hashed server-side to detect drift. */
  reviewedText: string | null | undefined;
  /**
   * Ask the output pane above to highlight these exact strings. Called with
   * an empty array to clear. Lives in the parent because the parent renders
   * the output.
   */
  onHighlight: (quotes: string[]) => void;
}

/** What the reviewer has entered. */
interface Draft {
  values: Partial<Record<CriterionKey, number>>;
  /**
   * Criteria the reviewer has scored themselves.
   *
   * Tracked rather than inferred by comparing against the judge: agreeing
   * with the AI is the commonest outcome, and value equality made that
   * indistinguishable from never having looked. The row stayed dashed and
   * marked AI however deliberately you pressed 5.
   */
  touched: CriterionKey[];
  tags: string[];
  notes: string;
  activeDimension: CriterionKey | null;
}

/**
 * Score one prompt test run against the active rubric.
 *
 * Docked under the run output, because the judgement is about the text
 * immediately above it. The rubric drives which dimensions appear — a
 * rubric with no tone criteria doesn't ask about tone.
 *
 * Local edits live in one nullable ``Draft``. While it is null the panel
 * renders the server's previous submission directly, so re-opening a
 * scored run shows what was submitted without an effect copying it into
 * state — and saving resets to null so the server stays the source of truth.
 */
export const RunScorePanel: FC<RunScorePanelProps> = ({ runId, reviewedText, onHighlight }) => {
  const { data, isFetching, refetch } = useGetEvalReviewForRun(runId);
  const scoreMutation = useScoreEvalReviewItem();

  const [draft, setDraft] = useState<Draft | null>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const startedAt = useRef<number>(0);

  useEffect(() => {
    startedAt.current = Date.now();
  }, [runId]);

  // The Orval fetcher resolves on 4xx rather than throwing, so the status
  // has to be checked before reading the body.
  const payload = data?.status === 200 ? data.data : undefined;
  const item = payload?.item ?? null;
  const standard = payload?.standard ?? null;
  const previous = item?.my_score ?? null;

  const allChecks = useMemo(() => item?.check_results ?? [], [item]);
  const ruleChecks = useMemo(() => allChecks.filter((check) => check.metric_type === MetricType.RULE), [allChecks]);

  // The server decides the standard — an authored rubric when there is one,
  // the built-in criteria otherwise — so the form never waits on setup. Every
  // key it sends is scored, including added criteria; ordering is the only
  // thing decided here.
  const scoredDimensions = useMemo(() => orderCriteria(standard?.dimensions.map((d) => d.dimension) ?? []), [standard]);

  // Per level rather than the joined line: the grid renders one row per
  // described score, which a joined "1 = …, 3 = …, 5 = …" cannot be split
  // back into reliably.
  const labelsByDimension = useMemo(
    () => Object.fromEntries((standard?.dimensions ?? []).map((d) => [d.dimension, d.label])),
    [standard],
  );

  const levelAnchorsByDimension = useMemo(
    () =>
      Object.fromEntries(
        (standard?.dimensions ?? []).map((d) => [d.dimension, (d.level_anchors ?? {}) as Record<string, string>]),
      ),
    [standard],
  );

  // The judge's 1-5 value per dimension, for pre-filling the grid.
  const judgeValues = useMemo<Partial<Record<CriterionKey, number>>>(
    () =>
      Object.fromEntries(
        (item?.judge_scores ?? [])
          .filter((entry) => entry.value !== null)
          .map((entry) => [entry.dimension, entry.value as number]),
      ),
    [item],
  );

  const judgeReasons = useMemo(
    () => Object.fromEntries((item?.judge_scores ?? []).map((e) => [e.dimension, e.reason])),
    [item],
  );

  // The text each dimension objected to. Highlighting these is the only way
  // a low score tells you *what* was wrong rather than just that something was.
  const judgeSpans = useMemo(
    () => Object.fromEntries((item?.judge_scores ?? []).map((e) => [e.dimension, e.spans])),
    [item],
  );

  /**
   * What the form shows before the reviewer touches anything.
   *
   * A previous submission wins — that is this reviewer's own answer. Failing
   * that, the judge's scores pre-fill, so the common case is reading five
   * rows and pressing Save rather than entering five values.
   */
  const submitted = useMemo<Draft>(
    () => ({
      values: previous
        ? Object.fromEntries(previous.dimension_scores.map((row) => [row.dimension, row.value]))
        : judgeValues,
      tags: Array.isArray(previous?.failure_tags) ? (previous.failure_tags as string[]) : [],
      touched: previous ? previous.dimension_scores.map((row) => row.dimension as CriterionKey) : [],
      notes: previous?.overall_notes ?? "",
      activeDimension: null,
    }),
    [previous, judgeValues],
  );

  const current = draft ?? submitted;
  const activeDimension = current.activeDimension ?? scoredDimensions[0] ?? null;

  // Follows the active row, so walking the grid with j/k walks the
  // highlights through the output above.
  useEffect(() => {
    onHighlight(activeDimension ? (judgeSpans[activeDimension] ?? []) : []);
  }, [activeDimension, judgeSpans, onHighlight]);

  useEffect(() => () => onHighlight([]), [onHighlight]);

  // Rows still showing the judge's number. Worth marking: a pre-filled grid
  // otherwise looks like work already done, and an unexamined judge score
  // would be saved as though a human had agreed with it.
  const fromJudge = useMemo(() => {
    if (previous) return new Set<string>();
    const touched = new Set(current.touched);
    return new Set(
      Object.entries(judgeValues)
        .filter(([dimension]) => !touched.has(dimension as CriterionKey))
        .map(([dimension]) => dimension),
    );
  }, [previous, judgeValues, current.touched]);

  const complete =
    scoredDimensions.length > 0 && scoredDimensions.every((dimension) => current.values[dimension] !== undefined);
  const canScore = item !== null && standard !== null;

  const update = (patch: Partial<Draft>) => setDraft({ ...current, ...patch });

  const scoreDimension = (dimension: CriterionKey, value: number) => {
    const values = { ...current.values, [dimension]: value };
    const touched = current.touched.includes(dimension) ? current.touched : [...current.touched, dimension];
    // Advance to the next unscored dimension so a reviewer can run the
    // number keys straight down the grid without reaching for the mouse.
    const next = scoredDimensions.find((d) => values[d] === undefined);
    update({ values, touched, activeDimension: next ?? dimension });
  };

  const toggleTag = (tag: string) =>
    update({
      tags: current.tags.includes(tag) ? current.tags.filter((t) => t !== tag) : [...current.tags, tag],
    });

  const save = async () => {
    if (!item || !complete) return;
    try {
      const result = await scoreMutation.mutateAsync({
        itemId: item.id,
        data: {
          dimension_scores: scoredDimensions.map((dimension) => ({
            dimension,
            value: current.values[dimension] as number,
          })),
          overall_notes: current.notes,
          failure_tags: current.tags,
          time_spent_seconds: Math.round((Date.now() - startedAt.current) / 1000),
          reviewed_text: reviewedText ?? "",
        },
      });
      if (result.status === 200) {
        notifySuccess("Score saved.");
        // Refetch BEFORE clearing the draft. Clearing first dropped `current`
        // back to the judge prefill with blank notes while `previous` was
        // still null — Save stayed enabled, and a second Enter overwrote the
        // stored score with the prefill. Until the fresh payload lands, the
        // draft keeps showing exactly what was saved, so a premature resave
        // can only resubmit the same values.
        //
        // And only clear the draft that was saved. The form is live while the
        // refetch is in flight, so an edit made in that window is a NEW draft
        // object (update() always allocates one) — wiping it would discard
        // keystrokes the reviewer made after the toast.
        const savedDraft = draft;
        await refetch();
        setDraft((latest) => (latest === savedDraft ? null : latest));
      } else {
        const detail = (result.data as { detail?: string } | undefined)?.detail;
        notifyError(detail ?? `Could not save the score (status ${result.status}).`);
      }
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Could not save the score.");
    }
  };

  // Single-key bindings, suppressed whenever a control has focus.
  //
  // The guard used to exclude only text inputs, which was too narrow for a
  // window-level handler: tabbing to any button on the page and pressing
  // Enter saved a score instead of activating the button, because
  // preventDefault cancelled the click. Arrow keys were cancelled page-wide,
  // so they stopped scrolling and stopped moving the profile select, and
  // q-t ate that select's type-ahead.
  //
  // So a focused control owns its keys, and these bindings apply when nothing
  // is focused — which is the state the reviewer is in while reading the
  // output above.
  useEffect(() => {
    if (!canScore) return;

    const handler = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (target && target !== document.body && typeof target.closest === "function") {
        const focusedControl = target.closest(
          "input, textarea, select, button, a[href], [contenteditable='true'], [role='button'], [role='textbox'], [role='option'], [role='menuitem'], [tabindex]:not([tabindex='-1'])",
        );
        if (focusedControl) return;
      }

      const numeric = Number(event.key);
      if (activeDimension && SCORE_VALUES.includes(numeric as (typeof SCORE_VALUES)[number])) {
        event.preventDefault();
        scoreDimension(activeDimension, numeric);
        return;
      }

      const index = activeDimension ? scoredDimensions.indexOf(activeDimension) : -1;
      const isNext = event.key === REVIEW_KEYS.NEXT_DIMENSION || event.key === REVIEW_KEYS.NEXT_DIMENSION_ARROW;
      const isPrev = event.key === REVIEW_KEYS.PREV_DIMENSION || event.key === REVIEW_KEYS.PREV_DIMENSION_ARROW;
      if (isNext && index >= 0) {
        event.preventDefault();
        update({
          activeDimension: scoredDimensions[Math.min(index + 1, scoredDimensions.length - 1)],
        });
      } else if (isPrev && index >= 0) {
        event.preventDefault();
        update({ activeDimension: scoredDimensions[Math.max(index - 1, 0)] });
      } else if (event.key === REVIEW_KEYS.NOTES) {
        event.preventDefault();
        notesRef.current?.focus();
      } else if (event.key === REVIEW_KEYS.SAVE && complete && !scoreMutation.isPending) {
        // isPending as well as complete, matching the Save button — Enter
        // pressed twice would otherwise submit the same score twice.
        event.preventDefault();
        void save();
      } else {
        const tag = FAILURE_TAGS.find((t) => t.key === event.key);
        if (tag) {
          event.preventDefault();
          toggleTag(tag.value);
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  if (isFetching && !payload) return null;

  // One card, sections divided by hairlines. Separate cards read as
  // unrelated blocks, and unframed text floats on a page whose other blocks
  // are all framed — hierarchy comes from the labels and type, not from some
  // sections having a border and others not.
  // A payload with no `standard` means the API predates it — almost always a
  // Django process that wasn't restarted. Say that, rather than leaving
  // someone staring at an empty form wondering what they failed to configure.
  const unavailable = !standard
    ? "The server is running older code than this page and sent no scoring standard. Restart it and reload."
    : scoredDimensions.length === 0
      ? "The scoring standard came back empty, so there is nothing to score against."
      : !item
        ? "This run finished before scoring existed, so there is nothing to score. Re-run the prompt."
        : null;

  return (
    // A pale rio wash over the whole panel — checks through to the key
    // legend — so the evaluating half of the page reads as one region rather
    // than as more run output. Every control inside stays white, which is
    // what keeps them reading as interactive against the tint.
    <div className="divide-y divide-rio-200 rounded-lg border border-rio-200 bg-bg-eval px-4">
      {item && <RuleCheckList checks={ruleChecks} onHighlight={onHighlight} />}

      {unavailable ? (
        <section className="py-4">
          <Label size="small" colorType="primary" className="uppercase tracking-wide">
            Eval score
          </Label>
          <Paragraph displayType="normal" size="small" className="mt-2 text-text-secondary">
            {unavailable}
          </Paragraph>
        </section>
      ) : null}

      {canScore && scoredDimensions.length > 0 && (
        <section className="flex flex-col gap-y-5 py-5">
          <div className="flex items-baseline justify-between gap-x-4">
            <Label size="small" colorType="primary" className="uppercase tracking-wide">
              Eval score
            </Label>
            {/* Only when a rubric is in force: which version a score was made
                against is real provenance, where "Default criteria" just
                labelled the absence of one. */}
            {standard.source === StandardSource.RUBRIC ? (
              <span className="text-xs text-text-tertiary">Rubric version {standard.version}</span>
            ) : null}
          </div>

          <ScoreGrid
            dimensions={scoredDimensions}
            levelAnchors={levelAnchorsByDimension}
            labels={labelsByDimension}
            values={current.values}
            fromJudge={fromJudge}
            judgeReasons={judgeReasons}
            spanCounts={Object.fromEntries(Object.entries(judgeSpans).map(([d, spans]) => [d, spans.length]))}
            activeDimension={activeDimension}
            disabled={scoreMutation.isPending}
            onScore={scoreDimension}
            onActivate={(dimension) => update({ activeDimension: dimension })}
          />

          <FailureTagPicker selected={current.tags} disabled={scoreMutation.isPending} onToggle={toggleTag} />

          <div>
            <Label size="small" colorType="primary" className="uppercase tracking-wide">
              Why
            </Label>
            <textarea
              ref={notesRef}
              value={current.notes}
              onChange={(event) => update({ notes: event.target.value })}
              rows={2}
              placeholder="What a prompt change would need to fix"
              className="mt-1.5 w-full rounded-md border border-border-medium bg-bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-border-focus"
            />
          </div>

          <div className="flex items-center justify-between gap-x-4">
            {/* "6 of 6 scored" was a count of a grid already on screen, and
                the judge pre-fills every row, so it read 6 of 6 from the
                moment the panel opened. What is left is the two things the
                grid does not say: why Save is unavailable, and that this run
                already has a score. */}
            <span className="text-xs text-text-tertiary">
              {!complete ? "Score every criterion to save" : previous ? "Saving again replaces what you submitted" : ""}
            </span>
            <Button
              text={scoreMutation.isPending ? "Saving…" : "Save score"}
              appearance="primary"
              className="shrink-0 rounded-sm"
              disabled={!complete || scoreMutation.isPending}
              onClick={save}
            />
          </div>

          <KeyLegend />
        </section>
      )}
    </div>
  );
};
